import { useUser } from "@/api/hooks/user";
import { ModelDropdown } from "@/components/molecules/ModelDropdown/ModelDropdown";
import { MultiSelectDropdown } from "@/components/molecules/MultiSelectDropdown/MultiSelectDropdown";
import {
  type BarbsIconTypeCustomOptions,
  aviationLayerConfig,
  barbsLayerConfig,
  isoLinesLayerConfig,
  jetStreamsLayerConfig,
  stationLayerConfig,
  symbolLayerConfigWWcode,
  symbolLayerConfigWeatherSymbol,
  weatherFrontsLayerConfig,
} from "@/constants/layerConfigAttributes";
import { HAZARD_TYPES } from "@/constants/newHazards";
import { FeatureHubGuard } from "@/featureFlags";
import type { LayerUnion, LayerUnionWithAll } from "@/layers";
import type { IsoBandsLayer } from "@/layers/geojson/isoLayerShared";
import {
  THUNDERSTORM_SEVERITY_ORDER,
  THUNDERSTORM_TYPE_ORDER,
  type ThunderstormSeverity,
  type ThunderstormType,
} from "@/layers/geojson/thunderstormConstants";
import {
  getBinaryAnomalyCommonModels,
  getBinaryAnomalyDefinition,
  getBinaryAnomalyFormulaLabel,
  getBinaryAnomalyLegendLabel,
  isBinaryAnomalyLayer,
} from "@/layers/png/binary/TemperatureAnomalyPoc";
import { type AviationTypesUnion, isMetar, isSigmet } from "@/layers/utility/aviationType";
import { type LayerKind, LayerKindName, type LayerWithParameterUnit } from "@/layers/utility/createLayerObject";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components";
import { IntegerInput } from "@/overlay/components/CommonParts/CustomInput";
import { Button } from "@/overlay/components/CommonParts/buttons";
import { DataResolutionSettings } from "@/overlay/components/DataResolutionSettings";
import { EarthquakeLayerUpdateForm } from "@/overlay/components/EarthquakeLayerUpdateForm/EarthquakeLayerUpdateForm";
import { AviationLegends } from "@/overlay/components/LayerStackWindow/AviationLegends";
import { CustomGeoJsonUpdateForm } from "@/overlay/components/LayerStackWindow/CustomGeoJsonForm";
import { InitDateField } from "@/overlay/components/LayerStackWindow/InitDateField";
import { InitTimeField } from "@/overlay/components/LayerStackWindow/InitTimeField";
import { GridLayerUpdateForm } from "@/overlay/components/LayerStackWindow/LayerConfig/GridLayerUpdateForm";
import { IsoBandsLayerForm, IsoLinesLayerForm } from "@/overlay/components/LayerStackWindow/LayerConfig/IsoLayerForms";
import {
  AviationTypeSelect,
  BarbsIconTypeInput,
  CalibratedToggle,
  ColorPickerInput,
  OpacityInput,
  StepConfigInput,
  WMSLayerSelect,
} from "@/overlay/components/LayerStackWindow/LayerFormElements";
import { AviationShowTimedOutCheckbox } from "@/overlay/components/LayerStackWindow/LayerFormElements/AviationShowTimedOutCheckbox";
import { IconSizeInput } from "@/overlay/components/LayerStackWindow/LayerFormElements/IconSizeInput";
import { ParameterInputForDevs } from "@/overlay/components/LayerStackWindow/ParameterInputForDevs";
import { PoiLayerPanel } from "@/overlay/components/LayerStackWindow/PoiLayerPanel";
import { TropicalCycloneFeatureSelect } from "@/overlay/components/LayerStackWindow/TropicalCycloneFeatureSelect";
import { ModelAndWeatherParameterForm } from "@/overlay/components/ModelAndWeatherParameterForm";
import { FormField, InputFormField } from "@/overlay/components/form-field";
import {
  setLayersIconItemProps,
  setLayersProps,
  setPoiLayerModelAndParameter,
  setPoiOptions,
  useMap,
} from "@/reducer/MapsState";
import { setSelectedLayerForAddParameter, setSelectedMap } from "@/reducer/UIState";
import { batchGroupBy } from "@/reducer/batchGroupBy";
import type { ColoringOptions, IconData, PoiOptions } from "@/reducer/client-models";
import type { PoiLayer } from "@/reducer/client-models/PoiLayer";
import { getFeatureValue } from "@/types/featureSet";
import type { MapId } from "@/types/type-utils";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import { getDefaultModelName } from "@/utility/layer";
import { normalizeWmsColormapOnParameterChange } from "@/utility/wmsColormap";
import {
  getSearchEngine,
  searchEnginesWeatherParams,
  stationModelAargau_TEMPORAL,
  stationModelGotthard_TEMPORAL,
  stationModelNationalstrassen_TEMPORAL,
  stationModelNsnw_TEMPORAL,
  stationModelZentras_TEMPORAL,
  stationMosModel_TEMPORAL,
  stationObsModel_TEMPORAL,
} from "@/weather-parameters";
import { getModelSchemFromStr } from "@/weather-parameters/lookup";
import { STATION_MODEL_ID } from "@/weather-parameters/staticStationModels";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  type AviationLayer,
  type BackgroundMapLayer,
  type BarbsLayer,
  type CartographicMapOutput,
  type CustomGeoJSONLayerOutput,
  type DrawingLayer,
  type EarthquakeLayer,
  type JetStreamsLayer,
  type LightningLayer,
  type NwaLayer,
  type PressureSystemLayer,
  ProjectionNameEnum,
  type StationLayer,
  type SymbolLayer,
  SymbolLayerType,
  type ThunderstormLayer,
  type TropicalCycloneLayer,
  VerticalInterpolationType,
  type WeatherFrontsLayer,
  type WmsLayer,
} from "@mm/metx-workbench.meteomatics.com";
import type { HasLayerIdxNarrowing, PartialWeatherParameter } from "@mm/weather-parameter-utils";
import * as weatherParameter from "@mm/weather-parameter-utils";
import { MmCheckbox } from "@mm/zeus-react";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { CustomColormapsManager } from "../../CustomColormapsManage/CustomColormapsManager";
import { LightningLayerUpdateForm } from "../../LightningLayerForm/LightningLayerUpdateForm";
import { Modal } from "../../window/Modal";
import "./style.scss";

export type LayerConfigType = { mapId: MapId; layer: LayerUnionWithAll; cartographicMap: CartographicMapOutput };

const listOfLayerKindWithoutInitTime: LayerKind[] = [
  "BackgroundMapDescription",
  "WeatherFrontsLayerDescription",
  "JetStreamsLayerDescription",
  "StationLayerDescription",
  "AviationLayerDescription",
  "LightningLayerDescription",
  LayerKindName.CustomGeoJSONLayerDescription,
  LayerKindName.NwaLayerDescription,
  LayerKindName.EarthquakeLayerDescription,
  LayerKindName.ThunderstormTrackingLayerDescription,
];

export function LayerConfig(props: LayerConfigType) {
  const hasInitTimeCheckboxVisible =
    !isBinaryAnomalyLayer(props.layer) &&
    !listOfLayerKindWithoutInitTime.includes(props.layer.kind as LayerKind) &&
    (
      props.layer as Exclude<
        typeof props.layer,
        | BackgroundMapLayer
        | AviationLayer
        | TropicalCycloneLayer
        | CustomGeoJSONLayerOutput
        | DrawingLayer
        | NwaLayer
        | EarthquakeLayer
        | ThunderstormLayer
      >
    ).model !== "mix";
  const newLayer = props.layer as Exclude<
    typeof props.layer,
    | BackgroundMapLayer
    | AviationLayer
    | TropicalCycloneLayer
    | CustomGeoJSONLayerOutput
    | DrawingLayer
    | NwaLayer
    | EarthquakeLayer
    | ThunderstormLayer
  >;

  return (
    <>
      <LayerConfigSwitch {...props} />
      {hasInitTimeCheckboxVisible && <InitTimeField mapId={props.mapId} layer={newLayer} />}
    </>
  );
}

export function LayerConfigSwitch(props: LayerConfigType) {
  const dispatch = useDispatch();
  const desktop = useDesktop();
  const { features } = useUser();
  const hasCustomColormaps = !!getFeatureValue(features, "tools.map.custom_colormap");

  // This is such a bullshit. We need whole map here, as it's used for adding new parameter to layer
  // Why just don't use layer directly?
  const map = useMap(props.mapId.map);
  const [showCustomColormapsManageWindow, setShowCustomColormapsManageWindow] = useState(false);

  function changeProps(propsValue: Partial<LayerUnion>, isGrouped?: boolean) {
    const normalizedProps =
      props.layer.kind === LayerKindName.WmsLayerDescription
        ? normalizeWmsColormapOnParameterChange(props.layer as WmsLayer, propsValue as Partial<WmsLayer>)
        : propsValue;
    const layerProps = { layerId: props.layer.id, id: props.mapId.map, props: normalizedProps };

    if (isGrouped) {
      batchGroupBy.start();
    }
    dispatch(setLayersProps(layerProps));
  }

  function changePoiLayerItemProps(newItem: IconData) {
    dispatch(setLayersIconItemProps({ layerId: props.layer.id, id: props.mapId.map, item: newItem }));
  }

  function changePoiLayerColoringOptionsProps(poiOptions: PoiOptions, options: ColoringOptions | undefined) {
    let param = {};
    // remove parameter_unit to prevent request
    if (options === undefined) {
      param = {
        parameter_unit: "",
      };
    }

    dispatch(
      setLayersProps({
        layerId: props.layer.id,
        id: props.mapId.map,
        props: {
          poiOptions: {
            ...poiOptions,
            coloringOptions: options,
          },
          ...param,
        },
      }),
    );
  }

  function onEditLayerParam() {
    function submitLayerParamChange(param: HasLayerIdxNarrowing<PartialWeatherParameter>) {
      const modelBefore = (props.layer as LayerWithParameterUnit).model;

      changeProps({
        parameter_unit: weatherParameter.parameterToString(param).formatted,
        model: getDefaultModelName(param, modelBefore),
      });
    }

    desktop.dispatch({
      type: "setWindowCustomProps",
      windowId: instanceId(WindowKind.AddParameter),
      windowKind: WindowKind.AddParameter,
      customProps: {
        source: "edit-param",
        onSubmitParam: submitLayerParamChange,
        searchEngines: [{ label: "Standard", searchEngine: getSearchEngine(props.layer) }],
      },
    });
    desktop.dispatch({
      type: "setWindowVisibility",
      windowId: instanceId(WindowKind.AddParameter),
      isVisible: true,
    });
  }

  function addPoiParameter(map: CartographicMapOutput, layer: PoiLayer) {
    function submitNewParam(
      map: CartographicMapOutput,
      param: HasLayerIdxNarrowing<PartialWeatherParameter>,
      model: string | null,
    ) {
      dispatch(
        setPoiLayerModelAndParameter({
          id: map.id,
          layerId: layer.id,
          props: {
            model: model || getDefaultModelName(param),
            parameter_unit: weatherParameter.parameterToString(param).formatted,
          },
        }),
      );
    }

    dispatch(setSelectedMap(map));
    dispatch(setSelectedLayerForAddParameter(props.layer));
    desktop.dispatch({
      type: "setWindowCustomProps",
      windowId: instanceId(WindowKind.AddParameter),
      windowKind: WindowKind.AddParameter,
      customProps: {
        source: "add-param",
        onSubmitParam: (param, _parameterType, model) => submitNewParam(map, param, model),
      },
    });
    desktop.dispatch({
      type: "setWindowVisibility",
      windowId: instanceId(WindowKind.AddParameter),
      isVisible: true,
    });
  }

  function handleUpdatePoiOptions(map: CartographicMapOutput, layerId: number, options: PoiOptions) {
    dispatch(
      setPoiOptions({
        id: map.id,
        layerId,
        poiOptions: options,
      }),
    );
  }

  // Why? At this point we will always have a map, as this dialog just simply can't show up without map layer
  if (!map) {
    return <></>;
  }

  switch (props.layer.kind) {
    case LayerKindName.WmsLayerDescription: {
      const wmsLayer = props.layer as WmsLayer;

      const anomalyDefinition = getBinaryAnomalyDefinition(wmsLayer);
      if (anomalyDefinition) {
        const commonModels = getBinaryAnomalyCommonModels(anomalyDefinition, (parameter) =>
          searchEnginesWeatherParams.standard.matchParameterNameExactly(parameter),
        );
        const isMercator =
          (props.cartographicMap.map_projection?.name ?? ProjectionNameEnum.mercator) === ProjectionNameEnum.mercator;

        return (
          <>
            <FormField label={t`Formula`}>
              <span>{getBinaryAnomalyFormulaLabel(anomalyDefinition)}</span>
            </FormField>
            <FormField label={t`Source`}>
              <ModelDropdown
                options={commonModels.map((model) => ({ id: model, title: model }))}
                value={wmsLayer.model}
                onChange={(model) => changeProps({ model })}
                placeholder={t`Select a model`}
                data-testid="temperature-anomaly-model-select"
              />
            </FormField>
            <WMSLayerSelect
              id={props.mapId}
              layer={wmsLayer}
              change={(propsValue) => changeProps(propsValue, false)}
              showCustomColormaps={hasCustomColormaps}
              additionalColorMaps={[
                {
                  value: anomalyDefinition.style.id,
                  label: `${getBinaryAnomalyLegendLabel(anomalyDefinition)} ${t`(default)`}`,
                },
              ]}
            />
            {hasCustomColormaps && (
              <>
                <FormField label={t`Colormaps`}>
                  <Button icon="folder_open" onClick={() => setShowCustomColormapsManageWindow(true)}>
                    <Trans>Manage</Trans>
                  </Button>
                </FormField>
                <Modal
                  isOpen={showCustomColormapsManageWindow}
                  onClose={() => setShowCustomColormapsManageWindow(false)}
                  closeOnBackdropClick={false}
                >
                  <CustomColormapsManager
                    initialColormapId={props.layer.color_map}
                    parameter={props.layer.parameter_unit}
                    onSave={changeProps}
                    onClose={() => setShowCustomColormapsManageWindow(false)}
                  />
                </Modal>
              </>
            )}
            {!isMercator && <p>{t`Anomaly layers are available on Mercator maps only.`}</p>}
            <OpacityInput
              layer={wmsLayer}
              change={changeProps}
              onStart={() => batchGroupBy.start()}
              onEnd={() => batchGroupBy.end()}
            />
          </>
        );
      }

      return (
        <>
          <FeatureHubGuard flag={"string_parmeter_input"}>
            <ParameterInputForDevs layer={props.layer} changeProps={changeProps} />
          </FeatureHubGuard>
          <ModelAndWeatherParameterForm layer={wmsLayer} mapId={props.mapId} onEditParameter={onEditLayerParam} />
          <WMSLayerSelect
            id={props.mapId}
            layer={wmsLayer}
            change={(propsValue) => changeProps(propsValue, false)}
            showCustomColormaps={hasCustomColormaps}
          />
          {hasCustomColormaps && (
            <>
              <FormField label={t`Colormaps`}>
                <Button icon="folder_open" onClick={() => setShowCustomColormapsManageWindow(true)}>
                  <Trans>Manage</Trans>
                </Button>
              </FormField>
              <Modal
                isOpen={showCustomColormapsManageWindow}
                onClose={() => setShowCustomColormapsManageWindow(false)}
                closeOnBackdropClick={false}
              >
                <CustomColormapsManager
                  initialColormapId={props.layer.color_map}
                  parameter={props.layer.parameter_unit}
                  onSave={changeProps}
                  onClose={() => setShowCustomColormapsManageWindow(false)}
                />
              </Modal>
            </>
          )}
          <OpacityInput
            layer={wmsLayer}
            change={changeProps}
            onStart={() => batchGroupBy.start()}
            onEnd={() => batchGroupBy.end()}
          />
          <DataResolutionSettings
            id={props.mapId}
            lod_bias={props.cartographicMap.lod_bias ?? 0}
            disabled={
              !!props.cartographicMap.map_projection?.name &&
              props.cartographicMap.map_projection?.name !== ProjectionNameEnum.mercator
            }
          />
          <FormField label={t`Downscale`}>
            <input
              type="checkbox"
              checked={props.layer.vertical_interpolation !== VerticalInterpolationType.none}
              onChange={() => {
                changeProps({
                  vertical_interpolation:
                    props.layer.vertical_interpolation === VerticalInterpolationType.none
                      ? VerticalInterpolationType.downscale
                      : VerticalInterpolationType.none,
                });
              }}
            />
          </FormField>
          {hasCalibratedAvailable(wmsLayer.parameter_unit) && (
            <CalibratedToggle value={!!props.layer.calibrated} change={changeProps} />
          )}
          <FeatureHubGuard flag="init_date">
            <InitDateField
              defaultValue={(wmsLayer.custom_options as any).init_date ?? ""}
              onChange={(propsValue: any) => {
                changeProps({
                  custom_options: {
                    ...wmsLayer.custom_options,
                    init_date: propsValue,
                  },
                });
              }}
            />
          </FeatureHubGuard>
          <FeatureHubGuard flag="api_url_suffix">
            <InitDateField
              label={t`API URL suffix`}
              defaultValue={(wmsLayer.custom_options as { api_url_suffix?: string } | undefined)?.api_url_suffix ?? ""}
              onChange={(propsValue: string) => {
                changeProps({
                  custom_options: {
                    ...wmsLayer.custom_options,
                    api_url_suffix: propsValue,
                  },
                });
              }}
            />
          </FeatureHubGuard>
        </>
      );
    }
    case LayerKindName.GridLayerDescription: {
      return (
        <GridLayerUpdateForm
          layer={props.layer}
          mapId={props.mapId}
          changeProps={changeProps}
          onEditLayerParam={onEditLayerParam}
        />
      );
    }
    case LayerKindName.IsoLinesLayerDescription: {
      return (
        <IsoLinesLayerForm
          layer={props.layer}
          mapId={props.mapId}
          changeProps={changeProps}
          onEditLayerParam={onEditLayerParam}
        />
      );
    }
    case LayerKindName.IsoBandsLayerDescription: {
      return (
        <IsoBandsLayerForm
          layer={props.layer as IsoBandsLayer}
          mapId={props.mapId}
          changeProps={changeProps}
          onEditLayerParam={onEditLayerParam}
        />
      );
    }
    case LayerKindName.WeatherFrontsLayerDescription: {
      const weatherFrontsLayer = props.layer as WeatherFrontsLayer;
      const initialModels = [
        getModelSchemFromStr("ecmwf-ifs"),
        getModelSchemFromStr("ncep-gfs"),
        getModelSchemFromStr("ukmo-um10"),
      ];
      const models = initialModels.filter((model): model is Exclude<typeof model, null> => model !== null);

      return (
        <>
          <FormField label={t`Source`}>
            <ModelDropdown
              options={models?.map((model) => ({ id: model.identifier, title: `${model.name}` })) || []}
              value={weatherFrontsLayer.model}
              onChange={(newModel) => {
                changeProps({ model: newModel });
              }}
              placeholder={"Select a model"}
            />
          </FormField>
          <FormField label={t`Line Width`}>
            <IntegerInput
              {...weatherFrontsLayerConfig.lineWidthLimits}
              onChange={(value) => changeProps({ line_width: value })}
              value={weatherFrontsLayer.line_width || 0}
            />
          </FormField>
          <InputFormField
            label={t`Icon Size`}
            type="range"
            onChange={(event) => changeProps({ text_size: Number(event.target.value) })}
            // Calculate the min & max in the input so increasing the input will result in smaller steps.
            {...weatherFrontsLayerConfig.iconSizeLimits}
            step={weatherFrontsLayerConfig.iconSizeStep}
            onMouseDown={() => batchGroupBy.start()}
            onMouseUp={() => batchGroupBy.end()}
            value={Number(weatherFrontsLayer.text_size)}
          />
        </>
      );
    }
    case LayerKindName.JetStreamsLayerDescription: {
      const jetStreamsLayer = props.layer as JetStreamsLayer;
      const showArrows = jetStreamsLayer.custom_options?.show_arrows ?? jetStreamsLayerConfig.defaultShowArrows;
      const arrowSize = jetStreamsLayer.custom_options?.arrow_size ?? jetStreamsLayerConfig.defaultArrowSize;

      return (
        <>
          <FormField label={t`Line Width`}>
            <IntegerInput
              {...jetStreamsLayerConfig.lineWidthLimits}
              onChange={(value) => changeProps({ line_width: value })}
              value={jetStreamsLayer.line_width || 0}
            />
          </FormField>
          <FormField label={t`Show Arrows`}>
            <MmCheckbox
              cid="jetstreams-show-arrows"
              data-testid="jetstreams-show-arrows"
              value="jetstreams-show-arrows"
              checked={showArrows}
              toggleHandler={() =>
                changeProps({
                  custom_options: {
                    ...jetStreamsLayer.custom_options,
                    show_arrows: !showArrows,
                  },
                })
              }
            />
          </FormField>
          {showArrows && (
            <InputFormField
              label={t`Arrow Size`}
              type="range"
              onChange={(event) =>
                changeProps({
                  custom_options: {
                    ...jetStreamsLayer.custom_options,
                    arrow_size: Number(event.target.value),
                  },
                })
              }
              onMouseDown={() => batchGroupBy.start()}
              onMouseUp={() => batchGroupBy.end()}
              {...jetStreamsLayerConfig.arrowSizeLimits}
              value={Number(arrowSize)}
            />
          )}
        </>
      );
    }
    case LayerKindName.EarthquakeLayerDescription: {
      const earthquakeLayer = props.layer as EarthquakeLayer;

      return (
        <>
          <EarthquakeLayerUpdateForm {...props} layer={earthquakeLayer} onChange={changeProps} />
        </>
      );
    }
    case LayerKindName.ThunderstormTrackingLayerDescription: {
      const thunderstormLayer = props.layer as ThunderstormLayer;
      const activeSeverityIds = (thunderstormLayer.custom_options.severity_filter ?? []).map((v) => String(v));
      const activeTypeIds = (thunderstormLayer.custom_options.types_filter ?? []).map((v) => String(v));
      const severityLabels: Record<ThunderstormSeverity, string> = {
        decaying: t`Decaying`,
        unclassified: t`Unclassified`,
        low: t`Low`,
        mid: t`Moderate`,
        high: t`High`,
      };
      const typeLabels: Record<ThunderstormType, string> = {
        history: t`Historical Track`,
        current: t`Active Cell`,
        nowcast: t`Impact Area`,
      };

      return (
        <>
          <OpacityInput
            layer={thunderstormLayer}
            change={changeProps}
            onStart={() => batchGroupBy.start()}
            onEnd={() => batchGroupBy.end()}
          />
          <FormField label={t`Severity filter`}>
            <MultiSelectDropdown
              items={[
                ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => ({
                  id: severity,
                  label: severityLabels[severity],
                })),
              ]}
              selectedIds={activeSeverityIds}
              onChange={(selected) => {
                const values = selected.map((item) => item);
                changeProps({
                  custom_options: {
                    ...thunderstormLayer.custom_options,
                    severity_filter: values,
                  },
                });
              }}
              placeholder={t`No filter applied`}
            />
          </FormField>
          <FormField label={t`Storm Stage`}>
            <MultiSelectDropdown
              items={[
                ...THUNDERSTORM_TYPE_ORDER.map((type) => ({
                  id: type,
                  label: typeLabels[type],
                })),
              ]}
              selectedIds={activeTypeIds}
              onChange={(selected) => {
                const values = selected.map((item) => item);
                changeProps({
                  custom_options: {
                    ...thunderstormLayer.custom_options,
                    types_filter: values,
                  },
                });
              }}
              placeholder={t`No filter applied`}
            />
          </FormField>
        </>
      );
    }
    case LayerKindName.BarbsLayerDescription: {
      const barbsLayer = props.layer as BarbsLayer;

      return (
        <>
          <ModelAndWeatherParameterForm layer={barbsLayer} mapId={props.mapId} />
          <StepConfigInput
            layer={barbsLayer}
            stepRange={{ ...barbsLayerConfig.stepRange }}
            id={props.mapId}
            change={changeProps}
          />
          <BarbsIconTypeInput
            value={barbsLayer.custom_options as BarbsIconTypeCustomOptions}
            onChange={(iconType) =>
              changeProps({
                custom_options: {
                  ...barbsLayer.custom_options,
                  icon_type: iconType,
                } as BarbsLayer["custom_options"] & BarbsIconTypeCustomOptions,
              })
            }
          />
          <IconSizeInput
            layer={barbsLayer}
            stepRange={{ ...barbsLayerConfig.iconSizeRange, step: 0.01 }}
            id={props.mapId}
            change={changeProps}
          />
          <ColorPickerInput
            label={t`Element Color`}
            change={(color) => changeProps({ element_color: color })}
            value={barbsLayer.element_color}
          />
          {hasCalibratedAvailable(barbsLayer.parameter_unit) && (
            <CalibratedToggle value={!!props.layer.calibrated} change={changeProps} />
          )}
        </>
      );
    }
    case LayerKindName.SymbolLayerDescription: {
      const symbolLayer = props.layer as SymbolLayer;
      const checkedShowOnlySignificantWeather = !!symbolLayer.custom_options?.show_only_significant_weather;

      const { layer_type: symbolLayerType } = symbolLayer;
      return (
        <>
          <ModelAndWeatherParameterForm layer={symbolLayer} mapId={props.mapId} />
          {/* Weather symbol layer */}
          {symbolLayerType === SymbolLayerType.WeatherSymbol && (
            <>
              <StepConfigInput
                layer={symbolLayer}
                stepRange={{ ...symbolLayerConfigWeatherSymbol.stepRange }}
                id={props.mapId}
                change={changeProps}
              />
              <IconSizeInput
                layer={symbolLayer}
                stepRange={{ ...symbolLayerConfigWeatherSymbol.iconSizeRange, step: 0.01 }}
                id={props.mapId}
                change={changeProps}
              />
            </>
          )}
          {symbolLayerType === SymbolLayerType.WeatherCode && (
            <>
              <StepConfigInput
                layer={symbolLayer}
                stepRange={{ ...symbolLayerConfigWWcode.stepRange }}
                id={props.mapId}
                change={changeProps}
              />
              <IconSizeInput
                layer={symbolLayer}
                stepRange={{ ...symbolLayerConfigWWcode.iconSizeRange, step: 0.01 }}
                id={props.mapId}
                change={changeProps}
              />
            </>
          )}

          <FormField label={t`only significant weather`}>
            <input
              type="checkbox"
              checked={checkedShowOnlySignificantWeather}
              onChange={() =>
                changeProps({
                  custom_options: {
                    ...symbolLayer.custom_options,
                    show_only_significant_weather: !checkedShowOnlySignificantWeather,
                  },
                })
              }
            />
          </FormField>
        </>
      );
    }
    case LayerKindName.StationLayerDescription: {
      const stationLayer = props.layer as StationLayer;
      // TODO-StationFeature: Remove the following code.
      // This is only to show a model on the Model select depending on the station layer model.
      let models: any;
      if (stationLayer.model === STATION_MODEL_ID.MM_MOS) {
        models = [stationMosModel_TEMPORAL];
      } else if (stationLayer.model === STATION_MODEL_ID.AARGAU) {
        models = [stationModelAargau_TEMPORAL];
      } else if (stationLayer.model === STATION_MODEL_ID.NATIONALSTRASSEN) {
        models = [stationModelNationalstrassen_TEMPORAL];
      } else if (stationLayer.model === STATION_MODEL_ID.ZENTRAS) {
        models = [stationModelZentras_TEMPORAL];
      } else if (stationLayer.model === STATION_MODEL_ID.GOTTHARD) {
        models = [stationModelGotthard_TEMPORAL];
      } else if (stationLayer.model === STATION_MODEL_ID.NSNW) {
        models = [stationModelNsnw_TEMPORAL];
      } else {
        models = [stationObsModel_TEMPORAL];
      }

      return (
        <>
          <FormField label={t`Source`}>
            <ModelDropdown
              options={models?.map((model: any) => ({ id: model.identifier, title: `${model.name}` })) || []}
              value={stationLayer.model}
              onChange={(newModel) => {
                changeProps({ model: newModel });
              }}
              placeholder={"Select a model"}
            />
          </FormField>
          <OpacityInput
            layer={stationLayer}
            change={changeProps}
            onStart={() => batchGroupBy.start()}
            onEnd={() => batchGroupBy.end()}
          />
          <ColorPickerInput
            label={t`Text Color`}
            change={(color) => changeProps({ text_color: color })}
            value={stationLayer.text_color ? stationLayer.text_color : "#000000"}
          />
          <FormField label={t`Text Size`}>
            <IntegerInput
              {...stationLayerConfig.textSizeLimits}
              onChange={(value) => changeProps({ text_size: value })}
              value={stationLayer.text_size || 0}
            />
          </FormField>
        </>
      );
    }
    case LayerKindName.GenericPoiLayerDescription: {
      const poiLayer = props.layer as PoiLayer;
      return (
        <>
          <PoiLayerPanel
            poiOptions={poiLayer.poiOptions}
            onChangePoiOptions={(options) =>
              handleUpdatePoiOptions(map, poiLayer.id, { ...poiLayer.poiOptions, ...options })
            }
            onChangeItem={(newItem: IconData) => changePoiLayerItemProps(newItem)}
            onChangeColoringOptions={(options) => changePoiLayerColoringOptionsProps(poiLayer.poiOptions, options)}
            onChangeLayerProps={changeProps}
            onEditParameter={onEditLayerParam}
            onAddParameter={() => addPoiParameter(map, poiLayer)}
            layer={poiLayer}
            mapId={props.mapId}
          />
        </>
      );
    }
    case LayerKindName.AviationLayerDescription: {
      const aviationLayer = props.layer as AviationLayer;
      return (
        <>
          <AviationTypeSelect
            aviationType={aviationLayer.aviation_type}
            change={(propsValue) => changeProps(propsValue)}
          />
          <InputFormField
            label={isSigmet(aviationLayer.aviation_type) ? t`Text Size` : t`Size`}
            type="range"
            onChange={(event) => changeProps({ text_size: Number(event.target.value) })}
            // Calculate the min & max in the input so increasing the input will result in smaller steps.
            {...aviationLayerConfig.sizeLimits}
            onMouseDown={() => batchGroupBy.start()}
            onMouseUp={() => batchGroupBy.end()}
            value={Number(aviationLayer.text_size)}
          />
          {isMetar(aviationLayer.aviation_type) && (
            <AviationShowTimedOutCheckbox
              showTimedOut={aviationLayer.custom_options.show_timed_out ?? aviationLayerConfig.defaultMetarShowTimedOut}
              change={() =>
                changeProps({
                  custom_options: {
                    show_timed_out: !(
                      aviationLayer.custom_options.show_timed_out ?? aviationLayerConfig.defaultMetarShowTimedOut
                    ),
                  },
                })
              }
            />
          )}

          <AviationLegends aviationType={aviationLayer.aviation_type as AviationTypesUnion} />
        </>
      );
    }
    case LayerKindName.PressureSystemLayerDescription: {
      const pressureSystemLayer = props.layer as PressureSystemLayer;
      return (
        <>
          <ModelAndWeatherParameterForm
            layer={pressureSystemLayer}
            mapId={props.mapId}
            onEditParameter={onEditLayerParam}
          />
          <InputFormField
            label={t`Values`}
            onChange={(event) => changeProps({ values: event.target.value })}
            value={pressureSystemLayer.values ?? undefined}
          />
          <InputFormField
            label={t`Range`}
            placeholder={t`Min,Max,Step`}
            onChange={(event) => changeProps({ value_range: event.target.value })}
            value={pressureSystemLayer.value_range ?? undefined}
          />

          <FormField label={t`Median filter`}>
            <IntegerInput
              {...isoLinesLayerConfig.gaussianAndMedianFilterLimits}
              onChange={(value) => changeProps({ filter_median: value })}
              value={pressureSystemLayer.filter_median || 0}
            />
          </FormField>
          <FormField label={t`Gaussian filter`}>
            <IntegerInput
              {...isoLinesLayerConfig.gaussianAndMedianFilterLimits}
              onChange={(value) => changeProps({ filter_gauss: value })}
              value={pressureSystemLayer.filter_gauss || 0}
            />
          </FormField>
          {hasCalibratedAvailable(pressureSystemLayer.parameter_unit) && (
            <CalibratedToggle value={!!props.layer.calibrated} change={changeProps} />
          )}
        </>
      );
    }
    case LayerKindName.LightningLayerDescription: {
      const fileteredModels = [getModelSchemFromStr("mix")].filter(
        (model): model is Exclude<typeof model, null> => model !== null,
      );
      return (
        <LightningLayerUpdateForm
          {...props}
          layer={props.layer as LightningLayer}
          onChange={changeProps}
          models={fileteredModels}
        />
      );
    }
    case LayerKindName.TropicalCycloneLayerDescription: {
      const tropicalCycloneLayer = props.layer as TropicalCycloneLayer;
      return (
        <>
          <TropicalCycloneFeatureSelect
            tropicalyCyclone={tropicalCycloneLayer.aviation_type}
            change={(propsValue) => changeProps(propsValue)}
          />
          <ColorPickerInput
            label={t`Icon Color`}
            change={(color) => changeProps({ line_color: color })}
            value={tropicalCycloneLayer.line_color}
          />
          <InputFormField
            label={t`Line Width`}
            type="range"
            onChange={(event) => changeProps({ line_width: Number(event.target.value) })}
            value={Number(tropicalCycloneLayer.line_width)}
            onMouseDown={() => batchGroupBy.start()}
            onMouseUp={() => batchGroupBy.end()}
            min={1}
            max={10}
          />
          <OpacityInput
            layer={tropicalCycloneLayer}
            change={changeProps}
            onStart={() => batchGroupBy.start()}
            onEnd={() => batchGroupBy.end()}
          />
        </>
      );
    }
    case LayerKindName.CustomGeoJSONLayerDescription:
      return (
        <CustomGeoJsonUpdateForm {...props} layer={props.layer as CustomGeoJSONLayerOutput} onChange={changeProps} />
      );
    case LayerKindName.NwaLayerDescription: {
      const nwaLayer = props.layer as NwaLayer;
      const activeIds = (nwaLayer.custom_options.alert_types_filter ?? []).map((v) => String(v));

      return (
        <>
          <OpacityInput
            layer={nwaLayer}
            change={changeProps}
            onStart={() => batchGroupBy.start()}
            onEnd={() => batchGroupBy.end()}
          />
          <FormField label={t`Hazard type filter`}>
            <MultiSelectDropdown
              items={HAZARD_TYPES.map((h) => ({ id: h.value, label: `${h.value} (${h.abbr})` }))}
              selectedIds={activeIds}
              onChange={(selected) => {
                const values = selected.map((item) => item);
                changeProps({
                  custom_options: {
                    ...nwaLayer.custom_options,
                    alert_types_filter: values,
                  },
                });
              }}
              placeholder={t`No filter applied`}
            />
          </FormField>
        </>
      );
    }
    case undefined:
      return null;
    default: {
      // @ts-expect-error
      const _exhaustive: never = props.layer.kind;
      return _exhaustive;
    }
  }
}
